/**
 * Referral Completion Modal - Trade Loop v2.0
 *
 * Allows the receiver to close out a referral with various outcomes
 */

import { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  CheckCircle2,
  DollarSign,
  MessageSquare,
  Send,
  X,
} from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useToast } from "@/contexts/ToastContext";

interface ReferralCompletionModalProps {
  referral: {
    id: number;
    clientName: string;
    senderName?: string;
  };
  isOpen: boolean;
  onClose: () => void;
}

type OutcomeType =
  | "completed"
  | "no_move_forward"
  | "no_contact"
  | "not_fit"
  | "other";

export default function ReferralCompletionModal({
  referral,
  isOpen,
  onClose,
}: ReferralCompletionModalProps) {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  const [outcome, setOutcome] = useState<OutcomeType | "">("");
  const [revenue, setRevenue] = useState<string>("");
  const [notes, setNotes] = useState("");
  const firstRadioRef = useRef<HTMLButtonElement>(null);

  // Focus the first radio button when modal opens to prevent keyboard popup on mobile
  useEffect(() => {
    if (isOpen) {
      // Small delay to ensure the modal is rendered
      const timer = setTimeout(() => {
        firstRadioRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const closeOutMutation = useMutation({
    mutationFn: async (data: {
      outcome: OutcomeType;
      revenue?: number;
      notes?: string;
    }) => {
      const response = await api.put(
        `/api/workflows/referral/${referral.id}/close-out`,
        data
      );
      return response;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/workflows/referral/list"] });
      queryClient.invalidateQueries({ queryKey: ["/api/workflows/dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["referrals"] });

      if (variables.outcome === "completed") {
        showSuccess(
          "Referral Completed",
          `The referral for ${referral.clientName} has been marked as complete.`
        );
      } else {
        showSuccess(
          "Referral Closed",
          `The referral for ${referral.clientName} has been closed.`
        );
      }
      handleClose();
    },
    onError: (error: any) => {
      showError("Close Out Failed", error.message || "Failed to close out referral");
    },
  });

  const handleClose = () => {
    // Reset form state
    setOutcome("");
    setRevenue("");
    setNotes("");
    onClose();
  };

  const handleSubmit = () => {
    if (!outcome) {
      showError("Selection Required", "Please select how the referral turned out");
      return;
    }

    const revenueNumber = revenue.trim() ? parseFloat(revenue) : undefined;
    if (outcome === "completed" && revenue.trim() && (isNaN(revenueNumber!) || revenueNumber! < 0)) {
      showError("Invalid Value", "Please enter a valid job value amount");
      return;
    }

    closeOutMutation.mutate({
      outcome: outcome as OutcomeType,
      revenue: outcome === "completed" ? revenueNumber : undefined,
      notes: notes.trim() || undefined,
    });
  };

  const canSubmit = !!outcome;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-lg bg-white/95 backdrop-blur-lg border-0 shadow-xl"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader className="border-b border-blue-100 pb-4 bg-gradient-to-r from-blue-50 to-indigo-50 -m-6 mb-0 p-6 rounded-t-lg">
          <DialogTitle className="flex items-center gap-2 text-gray-900 tracking-tight font-bold text-xl">
            <CheckCircle2 className="w-6 h-6 text-blue-600" />
            Close Out Referral
          </DialogTitle>
          <DialogDescription className="text-gray-700 mt-1">
            For <span className="font-semibold text-blue-900">{referral.clientName}</span>
            {referral.senderName && (
              <> from <span className="font-semibold text-blue-900">{referral.senderName}</span></>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Outcome Selection */}
          <div className="space-y-3">
            <Label className="font-semibold text-gray-900 text-base">
              How did this referral turn out?
            </Label>
            <RadioGroup value={outcome} onValueChange={(value) => setOutcome(value as OutcomeType)}>
              <div className="space-y-2">
                <div
                  className="flex items-center space-x-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => setOutcome("completed")}
                >
                  <RadioGroupItem ref={firstRadioRef} value="completed" id="completed" />
                  <Label htmlFor="completed" className="flex-1 cursor-pointer font-medium">
                    Completed Job
                  </Label>
                </div>
                <div
                  className="flex items-center space-x-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => setOutcome("no_move_forward")}
                >
                  <RadioGroupItem value="no_move_forward" id="no_move_forward" />
                  <Label htmlFor="no_move_forward" className="flex-1 cursor-pointer font-medium">
                    Customer didn't move forward
                  </Label>
                </div>
                <div
                  className="flex items-center space-x-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => setOutcome("no_contact")}
                >
                  <RadioGroupItem value="no_contact" id="no_contact" />
                  <Label htmlFor="no_contact" className="flex-1 cursor-pointer font-medium">
                    Could not reach customer
                  </Label>
                </div>
                <div
                  className="flex items-center space-x-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => setOutcome("not_fit")}
                >
                  <RadioGroupItem value="not_fit" id="not_fit" />
                  <Label htmlFor="not_fit" className="flex-1 cursor-pointer font-medium">
                    Not a good fit
                  </Label>
                </div>
                <div
                  className="flex items-center space-x-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => setOutcome("other")}
                >
                  <RadioGroupItem value="other" id="other" />
                  <Label htmlFor="other" className="flex-1 cursor-pointer font-medium">
                    Other
                  </Label>
                </div>
              </div>
            </RadioGroup>
          </div>

          {/* Job Value Input - Only show if completed */}
          {outcome === "completed" && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2 font-semibold text-gray-900">
                <DollarSign className="w-5 h-5 text-green-600" />
                Job Value (Optional)
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">
                  $
                </span>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Enter the revenue from this job"
                  value={revenue}
                  onChange={(e) => setRevenue(e.target.value)}
                  className="pl-8 bg-gray-50 border-gray-200 focus:border-green-500 focus:ring-green-500"
                />
              </div>
              <p className="text-sm text-gray-500">
                Used to track your referral revenue. Not shared with other partners.
              </p>
            </div>
          )}

          {/* Notes */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2 font-semibold text-gray-900">
              <MessageSquare className="w-5 h-5 text-gray-600" />
              Notes (Optional)
            </Label>
            <Textarea
              placeholder="Any additional details about the outcome..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="min-h-[100px] resize-none bg-gray-50 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4 border-t border-gray-200">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={closeOutMutation.isPending}
            className="flex-1 flex items-center justify-center gap-2"
          >
            <X className="w-4 h-4" />
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit || closeOutMutation.isPending}
            className={`flex-1 flex items-center justify-center gap-2 font-medium ${
              canSubmit
                ? "bg-blue-600 hover:bg-blue-700 text-white"
                : "bg-gray-200 text-gray-500 cursor-not-allowed"
            }`}
          >
            {closeOutMutation.isPending ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Closing...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Close Out Referral
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
